import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";
import InviteActions from "@/components/studio/InviteActions";

// Microphone Icon Component
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

// Notification type icons with colors
const getNotificationIcon = (type) => {
  switch (type) {
    case 'like': return { icon: '❤️', bg: 'bg-red-500/20', border: 'border-red-500/30' };
    case 'comment': return { icon: '💬', bg: 'bg-blue-500/20', border: 'border-blue-500/30' };
    case 'follow': return { icon: '👋', bg: 'bg-green-500/20', border: 'border-green-500/30' };
    case 'featured': return { icon: '🎖️', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' };
    case 'ar_review': return { icon: '🎯', bg: 'bg-gradient-to-r from-fuchsia-500/20 to-amber-500/20', border: 'border-fuchsia-500/40' };
    case 'push_milestone': return { icon: '🎉', bg: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/40' };
    case 'milestone': return { icon: '🏆', bg: 'bg-gradient-to-r from-purple-500/20 to-pink-500/20', border: 'border-purple-500/30' };
    case 'studio_invite':
    case 'studio_invite_accepted':
    case 'studio_invite_declined': return { icon: '🎙️', bg: 'bg-fuchsia-500/20', border: 'border-fuchsia-500/30' };
    case 'crew_invite': return { icon: '🤝', bg: 'bg-purple-500/20', border: 'border-purple-500/30' };
    case 'crew_join_request': return { icon: '🛎️', bg: 'bg-amber-500/20', border: 'border-amber-500/30' };
    case 'crew_request_approved': return { icon: '✅', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' };
    case 'crew_announcement': return { icon: '📣', bg: 'bg-fuchsia-500/20', border: 'border-fuchsia-500/40' };
    case 'crew_mention': return { icon: '🏷️', bg: 'bg-sky-500/20', border: 'border-sky-500/30' };
    case 'crew_reply': return { icon: '↩️', bg: 'bg-indigo-500/20', border: 'border-indigo-500/30' };
    case 'crew_post': return { icon: '🎙️', bg: 'bg-purple-500/20', border: 'border-purple-500/30' };
    case 'referral': return { icon: '🎉', bg: 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/40' };
    case 'reward': return { icon: '🪙', bg: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/40' };
    case 'report': return { icon: '📰', bg: 'bg-gradient-to-r from-indigo-500/20 to-fuchsia-500/20', border: 'border-fuchsia-500/40' };
    // Phase 21.1e — Live notification family.
    case 'live_start': return { icon: '🎤', bg: 'bg-gradient-to-r from-rose-500/20 to-pink-500/20', border: 'border-rose-500/40' };
    case 'live_gift_received': return { icon: '🎁', bg: 'bg-gradient-to-r from-amber-500/20 to-pink-500/20', border: 'border-amber-500/40' };
    case 'beam_request': return { icon: '🙋', bg: 'bg-amber-500/20', border: 'border-amber-500/40' };
    case 'beam_approved': return { icon: '✅', bg: 'bg-emerald-500/20', border: 'border-emerald-500/40' };
    case 'beam_declined': return { icon: '🙏', bg: 'bg-gray-500/20', border: 'border-gray-500/30' };
    case 'mod_muted': return { icon: '🔇', bg: 'bg-rose-500/20', border: 'border-rose-500/40' };
    case 'mod_kicked':
    case 'mod_removed_from_beam':
      return { icon: '👋', bg: 'bg-orange-500/20', border: 'border-orange-500/40' };
    case 'mod_banned': return { icon: '🚫', bg: 'bg-red-500/20', border: 'border-red-500/40' };
    case 'mod_promoted': return { icon: '🎛️', bg: 'bg-purple-500/20', border: 'border-purple-500/40' };
    case 'mod_demoted': return { icon: '⬇️', bg: 'bg-gray-500/20', border: 'border-gray-500/30' };
    case 'live_reminder': return { icon: '⏰', bg: 'bg-sky-500/20', border: 'border-sky-500/40' };
    // Phase 21.4 — Contest lifecycle notifications
    case 'contest_winner': return { icon: '🏆', bg: 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/40' };
    case 'contest_entered':
    case 'contest_entry_approved': return { icon: '✅', bg: 'bg-emerald-500/20', border: 'border-emerald-500/40' };
    case 'contest_entry_rejected': return { icon: '💌', bg: 'bg-gray-500/20', border: 'border-gray-500/30' };
    case 'contest_judging':
    case 'contest_closed':
    case 'contest_results': return { icon: '🏆', bg: 'bg-purple-500/20', border: 'border-purple-500/40' };
    default: return { icon: '🔔', bg: 'bg-purple-500/20', border: 'border-purple-500/30' };
  }
};

// Format relative time
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Get notification destination link
const getNotificationLink = (notification) => {
  const meta = notification.meta || {};
  if (meta.studio_url) return meta.studio_url;
  // iter_202 — Founding Artist invite deep-links to the claim page
  // using the token embedded in metadata.
  if (notification.type === 'founder_invitation') {
    const token = meta.invitation_token;
    if (token) return `/founder/claim/${token}`;
    return `/membership`;
  }
  if (notification.type === 'founder_granted') {
    return `/profile/me`;
  }
  // Phase 21.4 — Contest notifications deep-link to the correct destination.
  if (notification.type === 'contest_winner') {
    const slug = meta.contest_slug || meta.contest_id;
    const entryId = meta.entry_id;
    if (slug && entryId) return `/contests/${slug}/results/${entryId}`;
    if (slug) return `/contests/${slug}`;
  }
  if (['contest_entered', 'contest_entry_approved', 'contest_entry_rejected',
       'contest_judging', 'contest_closed', 'contest_results'].includes(notification.type)) {
    const slug = meta.contest_slug || meta.contest_id;
    if (slug) return `/contests/${slug}`;
  }
  // Phase 21.1e — All live notifications deep-link into the room itself.
  if (notification.live_room_id) {
    return `/beams/${notification.live_room_id}`;
  }
  // Phase 20.12 — Beat sold notification deep-links the producer to their
  // earnings dashboard. Use anchor #earnings so ProducerDashboardPage
  // scrolls / opens the right tab on arrival.
  if (notification.type === 'beat_sold') {
    return '/producer#earnings';
  }
  // Crew notifications — deep link with invite code when available so
  // CrewDetailPage can auto-apply the invite on arrival.
  if (notification.crew_id) {
    const code = notification.invite_code;
    return code
      ? `/crews/${notification.crew_id}?invite=${encodeURIComponent(code)}`
      : `/crews/${notification.crew_id}`;
  }
  if (notification.submission_id) {
    // Phase 9.7.19 — featured + push_milestone notifications append
    // ?from=featured so the TrackPage fires the confetti burst on arrival.
    if (notification.type === 'featured') {
      return `/track/${notification.submission_id}?from=featured`;
    }
    return `/track/${notification.submission_id}`;
  }
  if (notification.from_user_id && notification.type === 'follow') {
    return `/artist/${notification.from_user_id}`;
  }
  return null;
};

// Default avatar component
const DefaultAvatar = ({ name, size = "w-12 h-12" }) => {
  const initial = name?.charAt(0)?.toUpperCase() || '?';
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white font-bold text-lg`}>
      {initial}
    </div>
  );
};

const NotificationsPage = () => {
  const [user, setUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
        await fetchNotifications();
      } catch (error) {
        console.error('Auth check failed:', error);
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications?limit=100');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    const link = getNotificationLink(notification);
    if (link) {
      navigate(link);
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <MicIcon className="w-12 h-12 text-purple-500 animate-pulse" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="notifications-page">
      {/* Navigation */}
      <SharedNavigation currentUser={user} activePage="notifications" />

      {/* Main Content */}
      <div className="pt-20 px-4 pb-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Notifications</h1>
              <p className="text-gray-400">
                {unreadCount > 0 ? (
                  <span>You have <span className="text-purple-400 font-semibold">{unreadCount} unread</span> notification{unreadCount !== 1 ? 's' : ''}</span>
                ) : (
                  <span>You're all caught up!</span>
                )}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button
                onClick={markAllAsRead}
                variant="outline"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                data-testid="mark-all-read-btn"
              >
                Mark all as read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-4 bg-gray-900/50 border-purple-900/30 animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-700" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-700 rounded w-3/4" />
                      <div className="h-3 bg-gray-700 rounded w-1/4" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <Card className="p-12 bg-gray-900/50 border-purple-900/30 text-center" data-testid="no-notifications">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-xl font-semibold text-white mb-2">No notifications yet</h3>
              <p className="text-gray-400 mb-6">
                When someone likes your track, comments, or follows you, you'll see it here.
              </p>
              <Link to="/explore">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white">
                  Explore Music
                </Button>
              </Link>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="notifications-list">
              {notifications.map((notification) => {
                const iconStyle = getNotificationIcon(notification.type);
                const link = getNotificationLink(notification);
                
                return (
                  <Card
                    key={notification.notification_id}
                    className={`p-4 border transition-all duration-200 cursor-pointer hover:border-purple-500/50 ${
                      notification.is_read 
                        ? 'bg-gray-900/30 border-gray-800' 
                        : 'bg-purple-900/10 border-purple-900/40'
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Sender Avatar */}
                      <div className="relative flex-shrink-0">
                        {notification.from_user_photo ? (
                          <img
                            src={getProfilePhotoUrl({
                              profile_photo: notification.from_user_photo,
                              profile_photo_updated_at: notification.from_user_photo_updated_at,
                            })}
                            alt={notification.from_user_name || 'User'}
                            className="w-12 h-12 rounded-full object-cover border-2 border-purple-500/30"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <DefaultAvatar name={notification.from_user_name} />
                        )}
                        {/* Type badge overlay */}
                        <span className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-sm ${iconStyle.bg} border ${iconStyle.border}`}>
                          {iconStyle.icon}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {notification.title && (notification.type === 'ar_review' || notification.type === 'push_milestone' || notification.type === 'featured' || notification.type === 'milestone' || notification.type === 'live_start' || notification.type === 'live_gift_received' || notification.type === 'live_reminder') && (
                          <p className={`text-sm font-bold mb-1 ${notification.is_read ? 'text-purple-200' : 'text-fuchsia-300'}`}>
                            {notification.title}
                          </p>
                        )}
                        <p className={`text-sm leading-relaxed whitespace-pre-line ${notification.is_read ? 'text-gray-300' : 'text-white font-medium'}`}>
                          {notification.message}
                        </p>
                        {notification.type === 'studio_invite' && notification.meta?.project_id && (
                          <InviteActions
                            projectId={notification.meta.project_id}
                            inviteId={notification.meta.invite_id}
                            status={notification.meta.invite_status}
                          />
                        )}
                        {/* Phase 21.1e — Rich live card for any notification
                            that carries a live_room_id. Shows the live
                            cover (or a gradient fallback), the live title,
                            and a direct "Join Live" CTA. */}
                        {notification.live_room_id && (
                          <Link
                            to={`/beams/${notification.live_room_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-2 block rounded-xl overflow-hidden border border-rose-500/40 hover:border-rose-400 transition-colors"
                            data-testid={`live-notif-card-${notification.notification_id}`}
                          >
                            <div className="relative aspect-[16/9] bg-gradient-to-br from-rose-600/40 to-pink-600/40 flex items-end p-3">
                              {notification.live_cover_image && (
                                <img
                                  src={optimizeImage(notification.live_cover_image, 'card')}
                                  alt={notification.live_title || 'Live'}
                                  className="absolute inset-0 w-full h-full object-cover opacity-70"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div className="relative w-full flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="text-[9px] uppercase tracking-wider bg-rose-500 text-white px-1.5 py-0.5 rounded-full font-bold inline-block animate-pulse mb-1">
                                    LIVE
                                  </p>
                                  <p className="text-sm font-bold text-white truncate drop-shadow-md">
                                    {notification.live_title || 'Tap to join'}
                                  </p>
                                </div>
                                <span
                                  className="ml-2 flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-full bg-white text-rose-600 shadow-lg"
                                  data-testid={`live-notif-join-btn-${notification.notification_id}`}
                                >
                                  ▶ Join Live
                                </span>
                              </div>
                            </div>
                          </Link>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-xs text-gray-500">{formatTimeAgo(notification.created_at)}</span>
                          {notification.track_title && (
                            <span className="text-xs text-purple-400 truncate max-w-[200px]">
                              🎵 {notification.track_title}
                            </span>
                          )}
                          {notification.gift_quantity > 1 && (
                            <span
                              className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-500/30"
                              data-testid="live-notif-power-boost-badge"
                            >
                              ⚡ Power Boost ×{notification.gift_quantity}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-purple-500 rounded-full" data-testid="unread-indicator" />
                        )}
                        {link && (
                          <span className="text-gray-500 text-sm">→</span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.notification_id);
                          }}
                          className="p-1 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete notification"
                          data-testid={`delete-notification-${notification.notification_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-purple-900/30 bg-black/80 backdrop-blur-xl py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/community-guidelines" className="hover:text-purple-400 transition-colors">
                Community Guidelines
              </Link>
              <Link to="/explore" className="hover:text-purple-400 transition-colors">
                Explore
              </Link>
              <Link to="/dashboard" className="hover:text-purple-400 transition-colors">
                Dashboard
              </Link>
            </div>
            <p className="text-gray-500 text-sm">© 2025 RGM Studio™</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NotificationsPage;
